import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Hourglass, Ban } from 'lucide-react';
import { subscriptionPrice } from '../../utils/currency';
import { isSystemId } from '../../utils/systemId';
import ModalShell, { MODAL_CANCEL_CLASS, MODAL_SUBMIT_CLASS } from '../common/ModalShell';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const cap = (s) => String(s || '').replace(/^\w/, (c) => c.toUpperCase());

const METHOD_LABELS = {
  upi: 'UPI',
  bank_transfer: 'Bank transfer',
  cash: 'Cash',
  other: 'Other',
  invoice: 'Invoice — not paid yet',
};

// Full invoice for one subscription payment — plan, period, the price breakdown
// (list price, upgrade credit, net) + how it was paid. Works for old rows too
// (no originalAmount/creditApplied → just shows the amount, no credit line).
//
// Since 22 Sep 2026 (Q8) an invoice can be PENDING: a doctor requests a plan,
// this is what they are invoiced for, and the money arrives outside the
// platform by UPI or bank transfer. So the sheet no longer claims a card was
// charged — it names the method the admin recorded, and says what to do when
// nothing has been paid yet. Rows from the old simulated checkout still carry a
// cardLast4 and still print it, because that is what happened on them.
// "Billed to" names a person, never a system id (owner rule, 29 Sep 2026).
// Callers hand `doctorId` in two ways: SubscriptionPayments passes the doctor's
// NAME (falling back to the id), MySubscription passes the raw id. So the name
// on the payment row wins, the prop is used only when it is not that same id,
// and otherwise the neutral word stands in.
const billedToOf = (payment, doctorId) => {
  if (payment?.doctorName) return payment.doctorName;
  if (doctorId && doctorId !== payment?.doctorId && !isSystemId(doctorId)) return doctorId;
  return null;
};

const InvoiceModal = ({ payment, doctorId, onClose }) => {
  const { t } = useTranslation();
  if (!payment) return null;
  const net = parseFloat(payment.amount) || 0;
  const orig = payment.originalAmount !== '' && payment.originalAmount != null
    ? (parseFloat(payment.originalAmount) || net)
    : net;
  const credit = parseFloat(payment.creditApplied) || 0;
  const hasCredit = credit > 0;
  // The payment row names its currency; the invoice prints that sign (P24).
  const money = (n) => subscriptionPrice(n, payment, { grouped: true });
  const status = String(payment.status || 'paid');
  const isPending = status === 'pending';
  const isCancelled = status === 'cancelled';
  const methodLabel = payment.cardLast4
    ? `Card ···· ${payment.cardLast4}`
    : (METHOD_LABELS[payment.method] || cap(payment.method) || (isPending ? METHOD_LABELS.invoice : '—'));

  return (
    // Click on the dimmed page closes, as before the shared frame.
    <div onClick={(e) => { if (e.target === e.currentTarget.firstElementChild) onClose(); }}>
    <ModalShell
      title="Invoice"
      onClose={onClose}
      size="md"
      zClass="z-[110]"
      testId="invoice-modal"
      footer={(
        <>
          <button type="button" onClick={() => window.print()} className={MODAL_CANCEL_CLASS}>
            Print
          </button>
          <button type="button" onClick={onClose} className={MODAL_SUBMIT_CLASS}>
            Close
          </button>
        </>
      )}
    >
        <div className="space-y-4 text-sm">
          <div className="flex justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs text-slate-400">Reference</p>
              <p className="font-mono text-xs text-gray-700 break-all">{payment.reference || '—'}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs text-slate-400">Date</p>
              <p className="text-gray-700">{fmtDate(payment.createdAt)}</p>
            </div>
          </div>

          <div className="flex justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs text-slate-400">Billed to</p>
              <p className="text-gray-700 break-all" data-testid="invoice-billed-to">{billedToOf(payment, doctorId) || t('common.doctor_label', 'Doctor')}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs text-slate-400">Status</p>
              <span className={`inline-flex items-center gap-1 font-semibold ${isPending ? 'text-amber-600' : isCancelled ? 'text-slate-400' : 'text-emerald-600'}`} data-testid="invoice-status">
                {isPending ? <Hourglass className="w-4 h-4" /> : isCancelled ? <Ban className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                {cap(payment.status || 'paid')}
              </span>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3">
            <p className="font-semibold text-[#1a365d]">{payment.packageName || 'Subscription'}</p>
            <p className="text-xs text-slate-400">{fmtDate(payment.periodStart)} → {fmtDate(payment.periodEnd)}</p>
          </div>

          <div className="border-t border-gray-100 pt-3 space-y-1.5">
            <div className="flex justify-between text-gray-600">
              <span>Plan price</span>
              <span>{money(orig)}</span>
            </div>
            {hasCredit && (
              <div className="flex justify-between text-emerald-600" data-testid="invoice-credit">
                <span>Upgrade credit (unused days)</span>
                <span>−{money(credit)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-100 font-bold text-[#1a365d]">
              <span>{isPending ? 'Total due' : 'Total paid'}</span>
              <span data-testid="invoice-total">{money(net)}</span>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3 flex justify-between text-gray-600">
            <span className="text-slate-400">Payment method</span>
            <span data-testid="invoice-method">{methodLabel}</span>
          </div>

          {isPending && (
            <div className="flex items-start gap-2 text-xs text-[#2D8A8A] bg-[#4ABDBD]/10 border border-[#4ABDBD]/30 rounded-xl px-3 py-2" data-testid="invoice-how-to-pay">
              <span>Pay by UPI or bank transfer quoting the reference above. Your plan is activated once the payment is confirmed.</span>
            </div>
          )}
        </div>

    </ModalShell>
    </div>
  );
};

export default InvoiceModal;
